/**
 * Rasterises text clips to canvases. The result is cached (LRU) by text and style, so a static title
 * is drawn once and then only uploaded to the GPU when its content changes.
 */
import type { TextStyle } from '@ve/core';
import { LruCache } from '../util/lru.ts';

export interface RasterizedText {
  canvas: OffscreenCanvas | HTMLCanvasElement;
  width: number;
  height: number;
  /** Identity of the content; changes whenever the pixels change. */
  version: string;
}

type Ctx2D = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

function createCanvas(width: number, height: number): OffscreenCanvas | HTMLCanvasElement {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height);
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  return c;
}

export function fontString(style: TextStyle): string {
  return `${style.italic ? 'italic ' : ''}${style.fontWeight} ${style.fontSize}px ${style.fontFamily}`;
}

/** Cache key covering everything that affects the pixels. */
export function textCacheKey(text: string, style: TextStyle): string {
  return JSON.stringify([text, style]);
}

export class TextRasterizer {
  private cache = new LruCache<string, RasterizedText>(64);
  private measureCtx: Ctx2D | null = null;

  rasterize(text: string, style: TextStyle): RasterizedText {
    const key = textCacheKey(text, style);
    const hit = this.cache.get(key);
    if (hit) return hit;
    const result = this.draw(text, style, key);
    this.cache.set(key, result);
    return result;
  }

  clear(): void {
    this.cache.clear();
  }

  private measure(): Ctx2D {
    if (!this.measureCtx) {
      const ctx = createCanvas(1, 1).getContext('2d') as Ctx2D | null;
      if (!ctx) throw new Error('2D canvas is not available');
      this.measureCtx = ctx;
    }
    return this.measureCtx;
  }

  private draw(text: string, style: TextStyle, key: string): RasterizedText {
    const lines = text.split('\n');
    const font = fontString(style);
    const m = this.measure();
    m.font = font;
    const lineWidths = lines.map((l) => m.measureText(l).width);
    const contentW = Math.max(1, ...lineWidths);
    const lineH = style.fontSize * style.lineHeight;
    const contentH = Math.max(lineH, lines.length * lineH);
    // Margin so stroke and shadow are not clipped.
    const margin = Math.ceil(style.strokeWidth + style.shadowBlur * 2);
    const boxW = contentW + style.padding * 2;
    const boxH = contentH + style.padding * 2;
    const width = Math.max(1, Math.ceil(boxW + margin * 2));
    const height = Math.max(1, Math.ceil(boxH + margin * 2));

    const canvas = createCanvas(width, height);
    const ctx = canvas.getContext('2d') as Ctx2D | null;
    if (!ctx) throw new Error('2D canvas is not available');

    if (style.backgroundColor) {
      ctx.fillStyle = style.backgroundColor;
      ctx.fillRect(margin, margin, boxW, boxH);
    }
    ctx.font = font;
    ctx.textBaseline = 'middle';
    ctx.textAlign = style.align;
    ctx.lineJoin = 'round';
    const x =
      style.align === 'left'
        ? margin + style.padding
        : style.align === 'right'
          ? margin + style.padding + contentW
          : margin + style.padding + contentW / 2;
    lines.forEach((line, i) => {
      const y = margin + style.padding + lineH * (i + 0.5);
      if (style.shadowBlur > 0) {
        ctx.shadowColor = style.shadowColor;
        ctx.shadowBlur = style.shadowBlur;
      }
      if (style.strokeWidth > 0) {
        ctx.strokeStyle = style.strokeColor;
        ctx.lineWidth = style.strokeWidth * 2;
        ctx.strokeText(line, x, y);
        ctx.shadowBlur = 0;
        ctx.shadowColor = 'transparent';
      }
      ctx.fillStyle = style.color;
      ctx.fillText(line, x, y);
      ctx.shadowBlur = 0;
      ctx.shadowColor = 'transparent';
    });
    return { canvas, width, height, version: key };
  }
}
